import {
  Flex, Heading,
} from "@chakra-ui/react";
import { IntegrationPartner } from "definitions/constants";
import { NewIncomingWebhook } from "models/integration/incomingWebhook";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import { AvailableIntegrationType, getIntegrationLogoUrl } from "models/integration";
import useModal from "hooks/useModal";
import { useAddIntegration, useAvailableIntegrationListQuery } from "features/Integrations/hooks";
import { titleize } from "helpers/string";
import IntegrationSection from "./IntegrationSection";
import IntegrationCard from "./IntegrationCard";
import APIKeyCredentialsForm from "./APIKeyCredentialsForm";

type ConfirmationCopy = Record<IntegrationPartner, ConfirmationInfo>;

interface ConfirmationInfo {
  header: string;
  body: string;
  confirmButtonText: string;
  intent: "confirmation";
}

const CONFIRMATION_COPY: ConfirmationCopy = {
  greenhouse: {
    header: "Connect Greenhouse",
    body: "Credentials will be created that you will need to manually add to Greenhouse.",
    confirmButtonText: "Confirm",
    intent: "confirmation",
  },
  lever: {
    header: "Connect Lever",
    body: "You will be re-directed to Lever to give Camino approval to integrate.",
    confirmButtonText: "Confirm",
    intent: "confirmation",
  },
  slack: {
    header: "Confirm Connect",
    body: "You will be re-directed to Slack to give Camino approval to integrate.",
    confirmButtonText: "Confirm",
    intent: "confirmation",
  },
  ashby: {
    header: "Create Credientials in Ashby",
    body: "You will be re-directed to a Ashby to create an API key. Return here to enter your key.",
    confirmButtonText: "Confirm",
    intent: "confirmation",
  },
  google_calendar: {
    header: "Connect Google Calendar",
    body: "You will be re-directed to Google to give Camino approval to integrate.",
    confirmButtonText: "Confirm",
    intent: "confirmation",
  },
  zoom: {
    header: "Connect Zoom",
    body: "You will be re-directed to Zoom to give Camino approval to integrate.",
    confirmButtonText: "Confirm",
    intent: "confirmation",
  },
  roam: {
    header: "Connect Roam",
    body: "You will be re-directed to Roam to give Camino approval to integrate.",
    confirmButtonText: "Confirm",
    intent: "confirmation",
  },
};

// Wrapper component to handle confirmation for individual integrations
function AvailableIntegrationCard({
  integration,
  onConfirm,
}: {
  integration: AvailableIntegrationType;
  onConfirm: (integration: AvailableIntegrationType) => void;
}) {
  const confirmation = CONFIRMATION_COPY[integration.partner];

  return (
    <ButtonWithConfirm
      {...confirmation}
      handleConfirm={() => onConfirm(integration)}
    >
      <IntegrationCard
        name={titleize(integration.partner)}
        partner={integration.partner}
        logoUrl={getIntegrationLogoUrl(integration.partner)}
        description={integration.description}
        isConnected={false}
        connectButtonText="Connect"
      />
    </ButtonWithConfirm>
  );
}

interface IntegrationsProps {
  setNewWebhookCredentials: (webhook: NewIncomingWebhook) => void;
}

export default function AdditionalIntegrations({ setNewWebhookCredentials }: IntegrationsProps) {
  const { open: openAshbyCredsModal, close: closeAshbyCredsModal, Modal: AshbyCredsModal } = useModal({
    headerTitle: "Ashby Credentials",
    size: "lg",
  });

  const { data: additionalIntegrations, isLoading } = useAvailableIntegrationListQuery({
    placeholderData: [],
  });

  const { performRequest: createIntegration } = useAddIntegration(
    (response, _, toast) => {
      let toastMessage = "Integration Successfully Connected.";
      if (response.partner === "greenhouse") {
        setNewWebhookCredentials(response);
        toastMessage = "Integration Credentials Created.";
      } else if (response.partner === "ashby") {
        closeAshbyCredsModal();
      }
      toast({ title: toastMessage });
    },
  );

  const onConfirm = (integration: AvailableIntegrationType) => {
    switch (integration.partner) {
      case "google_calendar":
      case "lever":
      case "slack":
      case "zoom":
      case "roam":
        window.open(integration.url, "_blank", "noopener,noreferrer");
        break;
      case "greenhouse":
        createIntegration({
          integration: {
            partner: integration.partner,
            connectionType: "webhook",
          },
        });
        break;
      case "ashby":
        openAshbyCredsModal();
        window.open(integration.url, "_blank", "noopener,noreferrer");
        break;
      default:
        break;
    }
  };

  return (
    <>
      <IntegrationSection title="Additional" isLoaded={!isLoading}>
        <Flex direction="row" gap="4" wrap="wrap" width="100%">
          {additionalIntegrations?.length ? (
            additionalIntegrations.map((integration: AvailableIntegrationType) => (
              <AvailableIntegrationCard
                key={integration.partner}
                integration={integration}
                onConfirm={onConfirm}
              />
            ))
          ) : (
            <Heading size="xs" color="fg.muted">
              {isLoading ? "Loading" : "No available integrations"}
            </Heading>
          )}
        </Flex>
      </IntegrationSection>
      <AshbyCredsModal>
        <APIKeyCredentialsForm
          onSubmit={(data) => createIntegration({
            integration: {
              partner: "ashby",
              connectionType: "api_key",
              ...data,
            },
          })}
        />
      </AshbyCredsModal>
    </>
  );
}
